import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OpportunityDialog from "@/components/opportunities/OpportunityDialog";
import { Target, Loader2, User as UserIcon, CalendarClock, Plus, Search } from "lucide-react";
import { OPP_PRODUCTS, OPP_STAGES, OPP_CLOSED, PRIORITIES, LEAD_ORIGINS, priorityTone, eur, nextActionInfo } from "@/lib/crm";

const STAGE_ACCENT = {
  "Nuovo lead": "border-t-slate-400", "Contattato": "border-t-brand", "Appuntamento": "border-t-brand",
  "Sopralluogo": "border-t-brand", "Preventivo da preparare": "border-t-warning",
  "Preventivo inviato": "border-t-warning", "Follow-up": "border-t-warning",
  "Trattativa": "border-t-warning", "Vinta": "border-t-success", "Persa": "border-t-danger",
};

export default function OpportunitiesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [board, setBoard] = useState({ stages: [], columns: {}, totals: {} });
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState("");
  const [stage, setStage] = useState("");
  const [priority, setPriority] = useState("");
  const [leadOrigin, setLeadOrigin] = useState("");
  const [search, setSearch] = useState("");
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [officeId, setOfficeId] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [edit, setEdit] = useState(null); // opportunity being edited

  // Scroll orizzontale sticky sincronizzato con la board Kanban.
  const boardRef = useRef(null);
  const barRef = useRef(null);
  const [scrollW, setScrollW] = useState(0);
  const syncFromBoard = () => { if (barRef.current && boardRef.current) barRef.current.scrollLeft = boardRef.current.scrollLeft; };
  const syncFromBar = () => { if (boardRef.current && barRef.current) boardRef.current.scrollLeft = barRef.current.scrollLeft; };

  useEffect(() => {
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (product) params.product = product;
      if (stage) params.stage = stage;
      if (priority) params.priority = priority;
      if (leadOrigin) params.lead_origin = leadOrigin;
      if (search) params.search = search;
      if (officeId) params.office_id = officeId;
      if (operatorId) params.operator_id = operatorId;
      const { data } = await api.get("/opportunities/board", { params });
      setBoard(data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [product, stage, priority, leadOrigin, search, officeId, operatorId]);

  useEffect(() => { const t = setTimeout(load, search ? 250 : 0); return () => clearTimeout(t); }, [load]);

  // Tiene allineata la larghezza della barra sticky a quella reale della board.
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const update = () => setScrollW(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); window.removeEventListener("resize", update); };
  }, [board, loading]);

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="opportunities-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="ds-page-title flex items-center gap-2"><Target className="h-6 w-6 text-brand" /> Opportunità · Efficienza</h1>
          <p className="mt-1 text-sm text-slate-500">Pipeline commerciale fotovoltaico, accumulo, pompe di calore e altro. La prossima azione è sempre valorizzata.</p>
        </div>
      </div>

      {/* Filtri */}
      <div className="ds-card flex flex-wrap items-end gap-3 p-4">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca cliente…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="opp-filter-search" />
        </div>
        <Select value={product || "all"} onValueChange={(v) => setProduct(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[170px]" data-testid="opp-filter-product"><SelectValue placeholder="Prodotto" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti i prodotti</SelectItem>{OPP_PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={stage || "all"} onValueChange={(v) => setStage(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="opp-filter-stage"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti gli stati</SelectItem>{OPP_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={priority || "all"} onValueChange={(v) => setPriority(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[130px]" data-testid="opp-filter-priority"><SelectValue placeholder="Priorità" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutte</SelectItem>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={leadOrigin || "all"} onValueChange={(v) => setLeadOrigin(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="opp-filter-origin"><SelectValue placeholder="Provenienza" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Ogni provenienza</SelectItem>{LEAD_ORIGINS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={officeId || "all"} onValueChange={(v) => setOfficeId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px]" data-testid="opp-filter-office"><SelectValue placeholder="Sede" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutte le sedi</SelectItem>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
        {isAdmin && (
          <Select value={operatorId || "all"} onValueChange={(v) => setOperatorId(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]" data-testid="opp-filter-operator"><SelectValue placeholder="Consulente" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Tutti</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
      ) : (
        <div ref={boardRef} onScroll={syncFromBoard} className="flex gap-3 overflow-x-auto pb-3 kanban-hide-scrollbar" data-testid="kanban-board">
          {board.stages.map((stage) => {
            const cards = board.columns[stage] || [];
            return (
              <div key={stage} className="w-72 shrink-0" data-testid={`kanban-col-${stage}`}>
                <div className={`ds-card border-t-4 ${STAGE_ACCENT[stage] || "border-t-slate-300"} p-3`}>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">{stage}</span>
                    <span className="rounded-full bg-slate-100 px-2 text-xs font-bold text-slate-500">{board.totals[stage] || 0}</span>
                  </div>
                  <div className="space-y-2">
                    {cards.map((o) => (
                      <button key={o.id} onClick={() => setEdit(o)} data-testid={`opp-card-${o.id}`}
                        className="w-full rounded-lg border border-border bg-white p-3 text-left transition-shadow hover:shadow-md">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate font-semibold text-slate-800">{o.customer_name || "—"}</span>
                          <StatusBadge type={priorityTone(o.priority)}>{o.priority}</StatusBadge>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">{o.product} · {eur(o.estimated_value)}</div>
                        {!OPP_CLOSED.includes(o.stage) && (() => {
                          const n = nextActionInfo(o.next_action_date, !!o.next_action);
                          return n.label ? <div className="mt-1"><StatusBadge type={n.tone} testid={`opp-next-flag-${o.id}`}>{n.label}</StatusBadge></div> : null;
                        })()}
                        {o.close_date && <div className="mt-1 text-[11px] text-slate-400">Chiusa il {new Date(o.close_date).toLocaleDateString("it-IT")}</div>}
                        {o.next_action && !OPP_CLOSED.includes(o.stage) && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-brand">
                            <CalendarClock className="h-3 w-3" /> {o.next_action}{o.next_action_date ? ` · ${new Date(o.next_action_date).toLocaleDateString("it-IT")}` : ""}
                          </div>
                        )}
                        {o.consultant_name && <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-400"><UserIcon className="h-3 w-3" /> {o.consultant_name}</div>}
                      </button>
                    ))}
                    {cards.length === 0 && <div className="py-4 text-center text-xs text-slate-400">—</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Barra di scroll orizzontale sempre accessibile: sticky in basso alla
          finestra e sincronizzata con la board. Le colonne restano intatte. */}
      {!loading && (
        <div
          ref={barRef}
          onScroll={syncFromBar}
          className="ds-scroll sticky bottom-0 z-20 overflow-x-auto rounded-md border border-border bg-white/85 shadow-sm backdrop-blur"
          style={{ height: 16 }}
          data-testid="kanban-scrollbar"
          aria-hidden="true"
        >
          <div style={{ width: scrollW, height: 1 }} />
        </div>
      )}

      {edit && (
        <OpportunityDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} customerId={edit.customer_id}
          opportunity={edit} onSaved={() => { setEdit(null); load(); }} />
      )}
      <p className="text-xs text-slate-400">Per creare una nuova opportunità, aprila dalla scheda Cliente 360° → tab Efficienza.</p>
    </div>
  );
}
